import React, { useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { counterpartyOptionLabel, useCounterpartySearch } from '@/hooks/counterparty';
import { useCounterpartyTabs } from '@/hooks/trades';
import { useViewsStore } from '@/stores';
import type { View } from '@/types';
import { useFitTabs, visibleTabsOf } from '../mtm/fit-tabs';
import { ViewTab } from './ViewTab';

const MENU_BUTTON_CLASSES =
  'inline-flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-slate-500 bg-transparent rounded-md hover:bg-slate-200 hover:text-slate-800 motion-safe:transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

// Menu values: a tab already open is `view:<view id>`, a search hit is the counterparty id.
const VIEW_OPTION = 'view:';

const tabLabel = (view: View): string =>
  view.isInactive ? counterpartyOptionLabel(view.name, false) : view.name;

interface ViewTabBarProps {
  tradeCounts?: Record<string, number>;
  /** The counts are not in yet: each tab shows a placeholder instead of a number. */
  countsLoading?: boolean;
  /** The counts failed to load: each tab shows a dash, not a zero. */
  countsFailed?: boolean;
  /**
   * LR-700: right-aligned controls beside the tabs — the Term pill on History →
   * Trades, where there is no MetricsBar to host it (`resolveTradesChrome`:
   * `showMetricsBar: !isHistoryMode`). Rendered as a SIBLING of the measured tab
   * row, not inside it: the fit treats the row's width as "space available for
   * tabs", so a child would be counted as free space and tabs would overflow
   * under it. As a sibling it shrinks the row instead, and the ResizeObserver
   * already watching the row refits on its own.
   */
  trailing?: React.ReactNode;
}

/**
 * The tabs that fit the row, then a menu at the end of it: with an empty search it lists the
 * tabs that did not fit and pages through the rest of the server's tab list; a typed query
 * searches the WHOLE registry server-side, deactivated counterparties included. Picking one
 * without a tab opens a tab for it.
 */
export const ViewTabBar: React.FC<ViewTabBarProps> = ({
  tradeCounts = {},
  countsLoading = false,
  countsFailed = false,
  trailing,
}) => {
  const views = useViewsStore((s) => s.views);
  const openTabs = useViewsStore((s) => s.openTabs);
  const activeViewId = useViewsStore((s) => s.activeViewId);
  const activate = useViewsStore((s) => s.activate);
  const openCounterpartyTab = useViewsStore((s) => s.openCounterpartyTab);
  const closeTab = useViewsStore((s) => s.closeTab);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const tablistRef = useRef<HTMLDivElement>(null);
  // Closing from the keyboard unmounts the focused tab: hand focus to the tab now selected.
  const handleClose = (viewId: string) => {
    const hadFocus = tablistRef.current?.contains(document.activeElement) ?? false;
    closeTab(viewId);
    if (hadFocus) {
      requestAnimationFrame(() =>
        tablistRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus(),
      );
    }
  };
  const [query, setQuery] = useState('');
  const searching = query !== '';
  const search = useCounterpartySearch(query, false, { enabled: searching });
  // Same query as TradesPage's: its first page is the auto tabs, the rest is paged in here.
  const cpTabs = useCounterpartyTabs('trades');

  const tabs = useMemo(
    () =>
      openTabs
        .map((id) => views.find((v) => v.id === id))
        .filter((v): v is View => v !== undefined),
    [openTabs, views],
  );
  const activeIndex = tabs.findIndex((v) => v.id === activeViewId);
  const { rowRef, measureRef, menuMeasureRef, visibleCount, menuOpensLeft } = useFitTabs(
    activeIndex,
    tabs.length,
    tabs,
  );
  const visibleTabs = useMemo(
    () => visibleTabsOf(tabs, visibleCount, activeIndex),
    [tabs, visibleCount, activeIndex],
  );
  // In the order of the server's list, so a tab opened from it and pushed off the row is back
  // where it was. Tabs the loaded pages do not hold (a search pick, a page not loaded yet) lead.
  const hiddenOptions = useMemo(() => {
    const shown = new Set(visibleTabs.map((v) => v.id));
    const tabOf = new Map(tabs.map((v) => [v.counterpartyId, v]));
    const listed = new Set<string>();
    const rows = cpTabs.rows.flatMap((cp) => {
      const view = tabOf.get(cp.id);
      if (!view) return [{ value: String(cp.id), label: counterpartyOptionLabel(cp.name, cp.isActive) }];
      listed.add(view.id);
      return shown.has(view.id) ? [] : [{ value: `${VIEW_OPTION}${view.id}`, label: tabLabel(view) }];
    });
    const unlisted = tabs
      .filter((v) => !shown.has(v.id) && !listed.has(v.id))
      .map((v) => ({ value: `${VIEW_OPTION}${v.id}`, label: tabLabel(v) }));
    return [...unlisted, ...rows];
  }, [tabs, visibleTabs, cpTabs.rows]);
  // NOTE: the unloaded rest is a bare count, so a tab opened by hand whose counterparty sits on
  // a page not loaded yet is counted there too: twice while the tab is in the menu, and once
  // while it is in the row and not in the menu at all. The count corrects itself once that
  // page loads.
  const hiddenCount = hiddenOptions.length + Math.max(0, cpTabs.total - cpTabs.rows.length);

  const handleMenuChange = (value: string) => {
    if (value.startsWith(VIEW_OPTION)) {
      activate(value.slice(VIEW_OPTION.length));
      return;
    }
    const cp = [...(search.data?.pages.flatMap((page) => page.results) ?? []), ...cpTabs.rows].find(
      (row) => String(row.id) === value,
    );
    if (cp) openCounterpartyTab({ id: cp.id, name: cp.name, isActive: cp.isActive });
  };

  const renderTab = (view: View, onActivate: () => void) => (
    <ViewTab
      viewId={view.id}
      name={tabLabel(view)}
      isActive={view.id === activeViewId}
      isAutoGenerated={view.isAutoGenerated}
      tradeCount={countsLoading ? undefined : countsFailed ? null : (tradeCounts[view.id] ?? 0)}
      onActivate={onActivate}
      onClose={
        !view.isSystem && !view.isAutoGenerated ? () => handleClose(view.id) : undefined
      }
    />
  );

  return (
    <div data-tour="trades-view-tabs" className="flex items-end bg-slate-50 px-4 pt-3 pb-0">
      <div ref={rowRef} className="relative flex min-w-0 flex-1 items-center gap-1">
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute flex items-center"
          style={{ height: 0, overflow: 'hidden' }}
          aria-hidden="true"
        >
          {tabs.map((view) => (
            <div key={view.id} className="flex-shrink-0">
              {renderTab(view, () => {})}
            </div>
          ))}
        </div>
        {/* The menu button bare, and with the widest "+N more" it can show. */}
        <div
          ref={menuMeasureRef}
          className="pointer-events-none invisible absolute flex items-center"
          style={{ height: 0, overflow: 'hidden' }}
          aria-hidden="true"
        >
          <span className={MENU_BUTTON_CLASSES}>
            <ChevronDownIcon className="h-4 w-4" />
          </span>
          <span className={MENU_BUTTON_CLASSES}>
            <span className="tabular-nums">+{tabs.length + cpTabs.total} more</span>
            <ChevronDownIcon className="h-4 w-4" />
          </span>
        </div>
        <div ref={tablistRef} className="flex min-w-0 items-center gap-1" role="tablist" aria-label="View tabs">
          {visibleTabs.map((view, index) => {
            const separatorHidden =
              view.id === activeViewId ||
              visibleTabs[index - 1]?.id === activeViewId ||
              hoveredIndex === index - 1 ||
              hoveredIndex === index;
            return (
              <React.Fragment key={view.id}>
                {index > 0 && (
                  <div
                    aria-hidden="true"
                    className={clsx(
                      'h-4 w-px flex-shrink-0 motion-safe:transition-opacity duration-150',
                      separatorHidden ? 'opacity-0' : 'bg-slate-300/60',
                    )}
                  />
                )}
                <div
                  className="min-w-0 flex-shrink"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {renderTab(view, () => activate(view.id))}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {/* Always rendered, only hidden without tabs: the room reserved for the menu counts it. */}
          <div
            aria-hidden="true"
            className={clsx(
              'h-4 w-px flex-shrink-0',
              visibleTabs.length > 0 ? 'bg-slate-300/60' : 'opacity-0',
            )}
          />
          <SearchableSelect
            value={`${VIEW_OPTION}${activeViewId}`}
            onChange={handleMenuChange}
            options={searching ? search.options : hiddenOptions}
            onSearchChange={setQuery}
            isLoading={searching && search.isLoading}
            isError={searching && search.isError}
            hasMore={searching ? search.hasNextPage : cpTabs.hasNextPage}
            onLoadMore={() => void (searching ? search.fetchNextPage() : cpTabs.fetchNextPage())}
            isLoadingMore={searching ? search.isFetchingNextPage : cpTabs.isFetchingNextPage}
            searchPlaceholder="Search counterparties…"
            emptyMessage={searching ? undefined : 'Type to search all counterparties'}
            menuClassName={clsx(
              'top-full mt-1 w-72 border-slate-200',
              menuOpensLeft ? 'right-0' : 'left-0',
            )}
            triggerClassName={MENU_BUTTON_CLASSES}
            triggerContent={
              <>
                {hiddenCount > 0 && <span className="tabular-nums">+{hiddenCount} more</span>}
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </>
            }
            buttonProps={{
              'aria-label':
                hiddenCount > 0
                  ? `+${hiddenCount} more, search all counterparties`
                  : 'Search all counterparties',
              'data-testid': 'trades-view-tab-menu',
            }}
          />
        </div>
      </div>
      {trailing && <div className="flex flex-shrink-0 items-center pb-2 pl-2">{trailing}</div>}
    </div>
  );
};
